import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {finalize, switchMap, of} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {BlogApiService} from '../api/blog-api.service';
import {BLOG_CATEGORIES, Blog, BlogDraft} from '../models/blog.model';
import {SpinnerService} from '../shared/services/spinner.service';
import {can} from '../shared/access';

const MAX_COVER_BYTES = 5 * 1024 * 1024;

/** Write or edit a blog post (/blogs/new, /blogs/:id/edit). Members only; the API enforces this too. */
@Component({
    selector: 'app-blog-editor',
    templateUrl: './blog-editor.component.html',
    styleUrls: ['./blog-editor.component.css']
})
export class BlogEditorComponent implements OnInit {
    categories = BLOG_CATEGORIES;
    draft: BlogDraft = {title: '', category: 'ARTICLE', contentHtml: ''};
    editingId: string | null = null;
    existingCover?: string;
    coverFile: File | null = null;
    coverPreview: string | null = null;
    allowed = true;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private blogApi: BlogApiService,
        private spinner: SpinnerService,
        private toastr: ToastrService
    ) {
    }

    ngOnInit(): void {
        const perms = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.allowed = can(perms, 'BLOG', 'CREATE');
        const id = this.route.snapshot.paramMap.get('id');
        if (id && this.allowed) {
            this.editingId = id;
            this.spinner.show();
            this.blogApi.get(id).pipe(finalize(() => this.spinner.hide())).subscribe({
                next: (blog: Blog) => {
                    if (!blog.canEdit) {
                        this.toastr.error('Only the author can edit this post');
                        this.router.navigate(['/blogs', id]);
                        return;
                    }
                    this.draft = {title: blog.title, category: blog.category, contentHtml: blog.contentHtml || ''};
                    this.existingCover = blog.coverImageUri;
                },
                error: err => {
                    console.error('Failed to load blog for editing', err);
                    this.toastr.error('Could not load the post');
                    this.router.navigate(['/blogs']);
                }
            });
        }
    }

    onCoverPicked(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;
        if (file.size > MAX_COVER_BYTES) {
            this.toastr.error('Cover image must be 5 MB or smaller');
            return;
        }
        this.coverFile = file;
        const reader = new FileReader();
        reader.onload = () => this.coverPreview = reader.result as string;
        reader.readAsDataURL(file);
    }

    get canSave(): boolean {
        return !!this.draft.title.trim() && !!this.draft.contentHtml.replace(/<[^>]*>/g, '').trim();
    }

    save(): void {
        if (!this.canSave) return;
        this.spinner.show();
        const save$ = this.editingId ? this.blogApi.update(this.editingId, this.draft) : this.blogApi.create(this.draft);
        save$.pipe(
            // the cover is uploaded after the post exists, since the upload is addressed by blog id
            switchMap(blog => this.coverFile ? this.blogApi.uploadCover(blog.blogId, this.coverFile).pipe(
                switchMap(() => of(blog))) : of(blog)),
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: blog => {
                this.toastr.success(this.editingId ? 'Post updated' : 'Post published');
                this.router.navigate(['/blogs', blog.blogId]);
            },
            error: err => {
                console.error('Failed to save blog', err);
                this.toastr.error(err?.error?.message || 'Could not save the post');
            }
        });
    }
}
